import type { WorkflowChatTransport, WorkflowChatTransportOptions } from '@ai-sdk/workflow/client';
import type { ChatTransport, UIMessage, UIMessageChunk } from 'ai';

const DEFAULT_MAX_CONSECUTIVE_RECONNECT_ERRORS = 24;
const DEFAULT_RECONNECT_DELAY_MS = 1000;
const DEFAULT_MAX_RECONNECT_DELAY_MS = 5000;
const WORKFLOW_RUN_ID_HEADER = 'x-workflow-run-id';

class NoActiveWorkflowStreamError extends Error {
  constructor() {
    super('No active Nova workflow stream');
    this.name = 'NoActiveWorkflowStreamError';
  }
}

export interface DurableReconnectParameters {
  startIndex?: number;
  workflowRunId?: string;
}

export function getDurableReconnectParameters(body: unknown): DurableReconnectParameters {
  if (!body || typeof body !== 'object') {
    return {};
  }

  const { startIndex, workflowRunId } = body as Record<string, unknown>;

  return {
    startIndex:
      typeof startIndex === 'number' && Number.isInteger(startIndex) && startIndex >= 0 ? startIndex : undefined,
    workflowRunId: typeof workflowRunId === 'string' && workflowRunId.length > 0 ? workflowRunId : undefined,
  };
}

/**
 * A continuation turn (a clarification answer or tool handoff) continues an earlier assistant
 * message, and its stream holds only what it adds to that message.
 */
export function isNovaContinuationWorkflowRunId(workflowRunId: string): boolean {
  return workflowRunId.split('/')[3] === 'answer';
}

/** The running continuation a send reads instead of submitting a new turn. */
export function getDurableReattachWorkflowRunId(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') {
    return undefined;
  }

  const { reattachWorkflowRunId } = body as Record<string, unknown>;
  return typeof reattachWorkflowRunId === 'string' && reattachWorkflowRunId.length > 0
    ? reattachWorkflowRunId
    : undefined;
}

function normalizeChatUuid(chatUuid: string): string {
  return chatUuid.replace(/^new-chat-/, '');
}

function getUrlString(url: RequestInfo | URL): string {
  return typeof url === 'string' ? url : url instanceof URL ? url.href : url.url;
}

function getChatUuidFromReconnectUrl(url: RequestInfo | URL): string | undefined {
  const match = getUrlString(url).match(/\/chat\/completions\/([^/?#]+)\/stream(?:[?#].*)?$/);

  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

function addWorkflowRunId(url: RequestInfo | URL, workflowRunId: string | undefined): string {
  const urlString = getUrlString(url);
  if (!workflowRunId) {
    return urlString;
  }

  const parsed = new URL(urlString);
  parsed.searchParams.set('workflowRunId', workflowRunId);
  return parsed.toString();
}

function isRetryableReconnectResponse(response: Response): boolean {
  return response.status === 408 || response.status === 425 || response.status === 429 || response.status >= 500;
}

function delay(ms: number, abortSignal?: AbortSignal | null): Promise<void> {
  return new Promise((resolve) => {
    if (ms <= 0 || abortSignal?.aborted) {
      resolve();
      return;
    }

    const onDone = () => {
      clearTimeout(timeout);
      abortSignal?.removeEventListener('abort', onDone);
      resolve();
    };
    const timeout = setTimeout(onDone, ms);
    abortSignal?.addEventListener('abort', onDone);
  });
}

async function fetchReconnectWithRetry({
  fetchImplementation,
  getRequestInit,
  input,
  maxConsecutiveErrors,
  maxDelayMs,
  retryDelayMs,
  retry,
}: {
  fetchImplementation: typeof fetch;
  getRequestInit: () => Promise<RequestInit>;
  input: RequestInfo | URL;
  maxConsecutiveErrors: number;
  maxDelayMs: number;
  retryDelayMs: number;
  retry: boolean;
}): Promise<Response> {
  let attempt = 0;

  for (;;) {
    let requestInit: RequestInit | undefined;
    try {
      requestInit = await getRequestInit();
      const response = await fetchImplementation(input, requestInit);
      if (!retry || !isRetryableReconnectResponse(response) || attempt >= maxConsecutiveErrors) {
        return response;
      }

      await response.body?.cancel();
    } catch (error) {
      if (!retry || requestInit?.signal?.aborted || attempt >= maxConsecutiveErrors) {
        throw error;
      }
    }

    attempt += 1;
    await delay(Math.min(retryDelayMs * 2 ** Math.max(0, attempt - 1), maxDelayMs), requestInit?.signal);

    if (requestInit?.signal?.aborted) {
      throw requestInit.signal.reason ?? new DOMException('Aborted', 'AbortError');
    }
  }
}

interface ReconnectContext {
  headers?: HeadersInit;
  workflowRunId?: string;
  identifySubmittedWorkflow?: (workflowRunId: string | null) => void;
}

export interface DurableWorkflowSubmission {
  chatUuid: string;
  workflowRunId: Promise<string>;
}

function createLinkedAbortController(abortSignal?: AbortSignal | null): {
  abortController: AbortController;
  cleanup: () => void;
} {
  const abortController = new AbortController();
  const forwardAbort = () => abortController.abort(abortSignal?.reason);

  if (abortSignal?.aborted) {
    forwardAbort();
  } else {
    abortSignal?.addEventListener('abort', forwardAbort, { once: true });
  }

  return {
    abortController,
    cleanup: () => abortSignal?.removeEventListener('abort', forwardAbort),
  };
}

function wrapTransportStream({
  abortController,
  cleanup,
  stream,
  abortInitialUnavailable = false,
}: {
  abortController: AbortController;
  cleanup: () => void;
  stream: ReadableStream<UIMessageChunk>;
  abortInitialUnavailable?: boolean;
}): ReadableStream<UIMessageChunk> {
  const reader = stream.getReader();
  let sawChunk = false;
  let released = false;
  const release = () => {
    if (!released) {
      released = true;
      reader.releaseLock();
      cleanup();
    }
  };

  return new ReadableStream<UIMessageChunk>({
    async pull(controller) {
      try {
        const { done, value } = await reader.read();
        if (done || value === undefined) {
          release();
          controller.close();
          return;
        }
        sawChunk = true;
        controller.enqueue(value);
      } catch (error) {
        release();
        if (abortInitialUnavailable && !sawChunk && error instanceof NoActiveWorkflowStreamError) {
          // AI SDK's Chat treats AbortError as an expected no-op and returns before evaluating
          // sendAutomaticallyWhen. Closing an empty stream here would instead look like a
          // successful clarification continuation and could submit the resolved tool output again.
          controller.error(new DOMException(error.message, 'AbortError'));
        } else {
          controller.error(error);
        }
      }
    },
    async cancel(reason) {
      abortController.abort(reason);
      try {
        await reader.cancel(reason);
      } finally {
        release();
      }
    },
  });
}

export interface NovaWorkflowChatTransportOptions<TMessage extends UIMessage> {
  api: string;
  fetch?: typeof fetch;
  getReconnectApi: (chatId: string) => string;
  getStopApi: (chatId: string, workflowRunId?: string) => string;
  getToken: () => Promise<string | undefined>;
  maxConsecutiveReconnectErrors?: number;
  maxReconnectDelayMs?: number;
  onReconnectUnavailable?: (chatUuid: string) => void;
  onWorkflowAttached?: (details: { chatUuid: string; workflowRunId: string }) => void;
  onWorkflowSubmission?: (submission: DurableWorkflowSubmission) => void;
  prepareSendMessagesRequest: NonNullable<WorkflowChatTransportOptions<TMessage>['prepareSendMessagesRequest']>;
  reconnectDelayMs?: number;
}

/**
 * Adapts AI SDK's workflow reconnect protocol to Nova's DBOS route shape.
 * Parsing, framing repair, cursor advancement, and interrupted-stream continuation
 * remain owned by WorkflowChatTransport.
 */
export class NovaWorkflowChatTransport<TMessage extends UIMessage> implements ChatTransport<TMessage> {
  // A reconnect reads the workflow stream from its first chunk, so AI SDK resets
  // the message it resumes instead of appending the replayed parts to it.
  readonly resumeStreamIsReplay = true;

  private readonly activeReconnectAbortControllers = new Set<AbortController>();

  private readonly reconnectContexts: WeakMap<AbortSignal, ReconnectContext>;

  private readonly onWorkflowSubmission?: (submission: DurableWorkflowSubmission) => void;

  private workflowTransport?: Promise<WorkflowChatTransport<TMessage>>;

  private readonly createWorkflowTransport: () => Promise<WorkflowChatTransport<TMessage>>;

  private readonly stopWorkflowRequest: (request: { chatUuid: string; workflowRunId?: string }) => Promise<void>;

  constructor({
    api,
    fetch: fetchImplementation = globalThis.fetch,
    getReconnectApi,
    getStopApi,
    getToken,
    maxConsecutiveReconnectErrors = DEFAULT_MAX_CONSECUTIVE_RECONNECT_ERRORS,
    maxReconnectDelayMs = DEFAULT_MAX_RECONNECT_DELAY_MS,
    onReconnectUnavailable,
    onWorkflowAttached,
    onWorkflowSubmission,
    prepareSendMessagesRequest,
    reconnectDelayMs = DEFAULT_RECONNECT_DELAY_MS,
  }: NovaWorkflowChatTransportOptions<TMessage>) {
    const reconnectContexts = new WeakMap<AbortSignal, ReconnectContext>();

    const novaFetch: typeof fetch = async (input, init) => {
      const reconnectChatUuid = getChatUuidFromReconnectUrl(input);
      const normalizedChatUuid = reconnectChatUuid ? normalizeChatUuid(reconnectChatUuid) : undefined;
      const reconnectContext = init?.signal ? reconnectContexts.get(init.signal) : undefined;
      const workflowRunId = normalizedChatUuid ? reconnectContext?.workflowRunId : undefined;
      const requestUrl = addWorkflowRunId(input, workflowRunId);
      const baseHeaders = new Headers(reconnectContext?.headers);
      new Headers(init?.headers).forEach((value, key) => baseHeaders.set(key, value));
      if (init?.method === 'POST' && !baseHeaders.has('content-type')) {
        baseHeaders.set('content-type', 'application/json');
      }

      const response = await fetchReconnectWithRetry({
        fetchImplementation,
        getRequestInit: async () => {
          const headers = new Headers(baseHeaders);
          const token = await getToken();
          headers.set('authorization', `Bearer ${token}`);
          return { ...init, headers, method: normalizedChatUuid ? 'GET' : init?.method };
        },
        input: requestUrl,
        maxConsecutiveErrors: maxConsecutiveReconnectErrors,
        maxDelayMs: maxReconnectDelayMs,
        retry: Boolean(normalizedChatUuid && workflowRunId),
        retryDelayMs: reconnectDelayMs,
      });

      const responseWorkflowRunId = response.headers.get(WORKFLOW_RUN_ID_HEADER);
      if (normalizedChatUuid && response.status === 204) {
        onReconnectUnavailable?.(normalizedChatUuid);
        throw new NoActiveWorkflowStreamError();
      }

      if (normalizedChatUuid && responseWorkflowRunId && reconnectContext) {
        if (reconnectContext.workflowRunId && reconnectContext.workflowRunId !== responseWorkflowRunId) {
          await response.body?.cancel();
          throw new Error(
            `Nova reconnect returned workflow ${responseWorkflowRunId} while ${reconnectContext.workflowRunId} was requested`,
          );
        }
        reconnectContext.workflowRunId = responseWorkflowRunId;
        onWorkflowAttached?.({ chatUuid: normalizedChatUuid, workflowRunId: responseWorkflowRunId });
      }

      return response;
    };

    // Only the Nova v2 backend sends turns through this transport. Load it when a
    // turn needs it so AI SDK backends and idle chat views do not pay for it. The
    // `/client` entry keeps the package's workflow runtime out of the browser.
    this.createWorkflowTransport = async () => {
      const { WorkflowChatTransport } = await import('@ai-sdk/workflow/client');
      return new WorkflowChatTransport({
        api,
        fetch: novaFetch,
        maxConsecutiveErrors: maxConsecutiveReconnectErrors,
        onChatSendMessage(response, options) {
          const workflowRunId = response.headers.get(WORKFLOW_RUN_ID_HEADER);
          const reconnectContext = options.abortSignal ? reconnectContexts.get(options.abortSignal) : undefined;
          reconnectContext?.identifySubmittedWorkflow?.(workflowRunId);
          if (workflowRunId && reconnectContext) {
            reconnectContext.workflowRunId = workflowRunId;
            onWorkflowAttached?.({
              chatUuid: normalizeChatUuid(options.chatId),
              workflowRunId,
            });
          }
        },
        prepareReconnectToStreamRequest: ({ id }) => ({ api: getReconnectApi(id) }),
        prepareSendMessagesRequest,
      });
    };
    this.reconnectContexts = reconnectContexts;
    this.onWorkflowSubmission = onWorkflowSubmission;
    this.stopWorkflowRequest = async ({ chatUuid, workflowRunId }) => {
      const token = await getToken();
      const response = await fetchImplementation(getStopApi(normalizeChatUuid(chatUuid), workflowRunId), {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error((await response.text()) || `Failed to stop Nova durable workflow (${response.status})`);
      }
    };
  }

  private getWorkflowTransport(): Promise<WorkflowChatTransport<TMessage>> {
    return (this.workflowTransport ??= this.createWorkflowTransport());
  }

  /** Abort reconnect reads that outlive the chat view which started them. */
  abortActiveReconnects(): void {
    const activeControllers = [...this.activeReconnectAbortControllers];
    this.activeReconnectAbortControllers.clear();

    for (const abortController of activeControllers) {
      abortController.abort();
    }
  }

  /** Stop the server workflow while leaving its stream attached for terminal reconciliation. */
  async stopActiveWorkflow(request: { chatUuid: string; workflowRunId?: string }): Promise<void> {
    await this.stopWorkflowRequest(request);
  }

  async sendMessages(
    options: Parameters<ChatTransport<TMessage>['sendMessages']>[0],
  ): Promise<ReadableStream<UIMessageChunk>> {
    const reattachWorkflowRunId = getDurableReattachWorkflowRunId(options.body);
    if (reattachWorkflowRunId) {
      return this.reattachContinuation(options, reattachWorkflowRunId);
    }

    const { abortController, cleanup } = createLinkedAbortController(options.abortSignal);
    let identifyWorkflow!: (workflowRunId: string) => void;
    let rejectWorkflow!: (error: unknown) => void;
    const workflowRunId = new Promise<string>((resolve, reject) => {
      identifyWorkflow = resolve;
      rejectWorkflow = reject;
    });
    // Most submissions are never stopped. Still handle rejection when the POST fails or is
    // aborted before its headers arrive, so an unused identity promise cannot leak an error.
    void workflowRunId.catch(() => undefined);
    const rejectAbortedWorkflow = () => rejectWorkflow(abortController.signal.reason);
    abortController.signal.addEventListener('abort', rejectAbortedWorkflow, { once: true });
    this.reconnectContexts.set(abortController.signal, {
      headers: options.headers,
      identifySubmittedWorkflow: (id) => {
        abortController.signal.removeEventListener('abort', rejectAbortedWorkflow);
        if (id) {
          identifyWorkflow(id);
        } else {
          rejectWorkflow(new Error('Nova response did not identify the submitted workflow'));
        }
      },
    });
    // Notify synchronously, before token lookup, lazy transport loading, or network I/O. A
    // Stop in the submitted state must never discover or reuse the previous turn's ID.
    this.onWorkflowSubmission?.({ chatUuid: normalizeChatUuid(options.chatId), workflowRunId });

    try {
      const stream = await (
        await this.getWorkflowTransport()
      ).sendMessages({
        ...options,
        abortSignal: abortController.signal,
      });
      return wrapTransportStream({ abortController, cleanup, stream });
    } catch (error) {
      abortController.signal.removeEventListener('abort', rejectAbortedWorkflow);
      rejectWorkflow(error);
      cleanup();
      throw error;
    }
  }

  /**
   * Reads a running continuation turn from its start as the response to a send. AI SDK resumes a
   * stream into a fresh message but continues the last assistant message for a send, and a
   * continuation's stream only adds to that message.
   */
  private async reattachContinuation(
    options: Parameters<ChatTransport<TMessage>['sendMessages']>[0],
    workflowRunId: string,
  ): Promise<ReadableStream<UIMessageChunk>> {
    const stream = await this.reconnectToStream({
      chatId: options.chatId,
      headers: options.headers,
      metadata: options.metadata,
      body: { workflowRunId, startIndex: 0 },
    });
    if (stream === null) {
      // An empty stream would read as a finished continuation; AbortError is AI SDK's no-op.
      throw new DOMException('No active Nova workflow stream', 'AbortError');
    }

    return stream;
  }

  async reconnectToStream(
    options: Parameters<ChatTransport<TMessage>['reconnectToStream']>[0],
  ): Promise<ReadableStream<UIMessageChunk> | null> {
    const { startIndex, workflowRunId } = getDurableReconnectParameters(options.body);
    const { abortController, cleanup: cleanupAbortLink } = createLinkedAbortController();
    this.activeReconnectAbortControllers.add(abortController);
    const cleanup = () => {
      this.activeReconnectAbortControllers.delete(abortController);
      cleanupAbortLink();
    };
    this.reconnectContexts.set(abortController.signal, {
      headers: options.headers,
      workflowRunId,
    });

    try {
      const stream = await (
        await this.getWorkflowTransport()
      ).reconnectToStream({
        ...options,
        abortSignal: abortController.signal,
        startIndex,
      });
      if (stream === null) {
        cleanup();
        return null;
      }

      // WorkflowChatTransport starts its fetch lazily when the consumer reads.
      // Keep that property so useChat owns cancellation before reconnect/backoff begins.
      return wrapTransportStream({
        abortController,
        abortInitialUnavailable: true,
        cleanup,
        stream,
      });
    } catch (error) {
      cleanup();
      throw error;
    }
  }
}
